import { trackEvent } from '@patrianna/analytics'
import { IconButton } from '@patrianna/core-components'
import type { GameProduct, GameProductShort } from '@patrianna/shared-patrianna-types/store/GameModule'
import { Heart, HeartOutline } from '@patrianna-icons/material-icons'
import cx from 'classnames'
import { useCallback } from 'react'
import type { MouseEvent } from 'react'

import { useAppDispatch, useAppSelector } from 'store/hooks'
import { addFavoriteGame, removeFavoriteGame } from 'store/modules/favorites/actions'
import { isFavoriteGameSelector } from 'store/modules/favorites/selectors'
import { openSnackbar } from 'store/modules/snackbar/actions'
import { isLoggedInSelector } from 'store/modules/user/selectors'

import FavoriteSnackbar from './FavoriteSnackbar'
import classes from './styles.module.scss'

export type FavouriteButtonProps = {
  gameProduct?: GameProduct | GameProductShort
  className?: string
  refererPath?: 'game_tile' | 'game_opening' | 'game_description' | 'game_play'
  variant?: 'default' | 'gamePlayVerticalHeader' | 'gamePlayMobileHeader' | 'gamePlayFooter'
}

export default function FavoriteButton({
  gameProduct,
  className,
  refererPath = 'game_tile',
  variant = 'default',
}: FavouriteButtonProps) {
  const isLoggedIn = useAppSelector(isLoggedInSelector)
  const isFavorite = useAppSelector((state) => isFavoriteGameSelector(state, gameProduct?.code))
  const dispatch = useAppDispatch()

  const handleSnackbar = useCallback(
    (action: 'added' | 'removed') => {
      dispatch(
        openSnackbar({
          message: FavoriteSnackbar,
          componentProps: { action },
          variant: 'info',
          positionVertical: 'bottom',
          positionHorizontal: 'center',
          autoHide: 10000,
        }),
      )
    },
    [dispatch],
  )

  const onFavoriteClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()

      handleSnackbar(isFavorite ? 'removed' : 'added')

      if (isFavorite) {
        dispatch(removeFavoriteGame(gameProduct))
        trackEvent(`${refererPath}_unfavorite_click`)
      } else {
        dispatch(addFavoriteGame(gameProduct))
        trackEvent(`${refererPath}_favorite_click`)
      }
    },
    [isFavorite, gameProduct, handleSnackbar, refererPath],
  )

  if (!gameProduct || !isLoggedIn) {
    return null
  }

  const buttonClasses = cx(classes.favoriteButton, variant !== 'default' && classes[variant], className)

  return (
    <IconButton className={buttonClasses} onClick={onFavoriteClick}>
      {isFavorite ? <Heart className={classes.filledHeart} /> : <HeartOutline />}
    </IconButton>
  )
}
